import type { ContextShape } from '@ardoq/data-model';
import {
  type TraverseOptions,
  type TraversalAndGroupingResult,
  getRelevantComponentIds,
  getGraphEdgeLabelForWorkspaceMode,
  getGraphNodeOrGroupLabelForWorkspaceMode,
  getEmptyTraversalAndGroupingResult,
} from '@ardoq/graph';
import { isComponentIdIncludedInContextByFilter } from '@ardoq/component-interface';
import { componentOperations, toIds } from '@ardoq/core';
import type {
  ComponentsState,
  ScenarioRelatedComponentIds,
} from 'streams/components/components$';
import { hasGroupByParentAll } from 'modelInterface/graph/utils';
import {
  buildTraversalViewModel,
  getTraversalConfiguration,
  getScenarioGroup,
} from 'tabview/graphViews/traversalViewModel';
import {
  TRAVERSE_ALL_BASE,
  TRAVERSE_PARENTS_REFTYPES,
} from 'modelInterface/graph/consts';
import { buildGraph } from 'modelInterface/graph/buildGraphForWorkspaceMode';
import { emptyReferenceFilterContext } from '@ardoq/filter-interface';
import {
  BuildViewModelArgs,
  BuildViewModelResult,
  BuildViewModelWithDataLimitArgs,
  ViewDataLimitCheck,
} from './types';
import {
  getExceededViewDataLimit,
  type ViewDataLimitExceeded,
} from 'views/environmentals/viewDataLimits';
import type { ViewIds } from '@ardoq/api-types';
import { viewModelFromGraph } from './viewModelFromGraph';
import { isTruthy } from '@ardoq/common-helpers';
import { resolveEdgeReferenceDisplayLabel } from 'modelInterface/graph/resolveEdgeReferenceDisplayLabel';

const TRAVERSE_ALL_PARENTS: TraverseOptions = {
  ...TRAVERSE_ALL_BASE,
  incomingReferenceTypes: TRAVERSE_PARENTS_REFTYPES,
  outgoingReferenceTypes: new Set(),
  excludedComponentIds: new Set(),
};

const getStartSet = (
  context: ContextShape,
  components: ComponentsState,
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds
): string[] => {
  // Component context resolves directly, but a scenario context takes
  // precedence — it expands across all scenario workspaces below.
  if (context.componentId && !context.scenarioId) {
    return isComponentIdIncludedInContextByFilter(context.componentId)
      ? [context.componentId]
      : getRelevantComponentIds({
          ...context,
          childrenByParent: components.childrenByParent,
        });
  }

  const workspaceIds = context.scenarioId
    ? context.workspaceIds
    : context.workspaceId
      ? [context.workspaceId]
      : [];

  const placeholderModels = components.models.filter(
    component => !scenarioRelatedComponentIds.has(component._id)
  );

  return workspaceIds.flatMap(workspaceId =>
    toIds(componentOperations.getRootComponents(workspaceId, placeholderModels))
  );
};

export const getRawGraph = ({
  context,
  components,
  scenarioRelatedComponentIds,
  viewSettings,
  graphModel,
  loadedScenarioData,
  scenarioRelatedComponents,
  showScopeRelated,
  overrideTraverseOptions = ({ menuConfig }) => menuConfig,
  viewId,
  viewInterface,
  i18nLocale,
}: BuildViewModelArgs) => {
  const startSet = getStartSet(
    context,
    components,
    scenarioRelatedComponentIds
  );

  const scenarioGroup = getScenarioGroup({
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
  });

  const groupingRules = [
    scenarioGroup,
    ...viewInterface.getAllGroupingRules(),
  ].filter(isTruthy);

  const { traverseOptions, referenceTypeMap } = getTraversalConfiguration({
    viewSettings,
    graphModel,
    scenarioRelatedComponentIds,
  });
  const sequentialTraverseOptions = hasGroupByParentAll(groupingRules)
    ? [TRAVERSE_ALL_PARENTS]
    : [];

  const traversalViewModel = buildTraversalViewModel({
    graphInterface: viewInterface,
    graphModel,
    startSet,
    traverseOptions,
  });

  // Currently there are three levels of graph items:
  // the raw data objects returned from `buildGraph`,
  // the `GraphItem`s (with the according subclasses) serving as binding
  // layer to the existing code and the actual yFile nodes.

  // Raw graph data
  const rawGraph = buildGraph({
    graphInterface: viewInterface,
    graph: graphModel,
    startSet,
    groupingRules,
    traverseOptions: overrideTraverseOptions({
      menuConfig: traverseOptions,
      referenceTypeMap,
    }),
    viewId,
    sequentialTraverseOptions,
    includeOnlyConnectedComponents: viewSettings.showOnlyConnectedComponents,
    useNewGrouping: viewSettings.useNewGrouping,
    i18nLocale,
    ctx: emptyReferenceFilterContext(),
  });

  return { rawGraph, traversalViewModel };
};

type RefusedDataLimitArgs = {
  rawGraph: TraversalAndGroupingResult;
  dataLimitCheck: ViewDataLimitCheck;
  viewId: ViewIds;
};

const getRefusedDataLimit = ({
  rawGraph,
  dataLimitCheck: { isEnabled, isPresentationMode, hasUserBypassed },
  viewId,
}: RefusedDataLimitArgs): ViewDataLimitExceeded | null => {
  if (!isEnabled) {
    return null;
  }
  return getExceededViewDataLimit({
    viewId,
    itemCount: rawGraph.componentMap.size + rawGraph.referenceList.length,
    isPresentationMode,
    hasUserBypassed,
  });
};

const buildViewModel = ({
  viewInterface,
  context,
  components,
  scenarioRelatedComponentIds,
  viewSettings,
  graphModel,
  loadedScenarioData,
  scenarioRelatedComponents,
  isInScopeDiffMode,
  expandedStateChangingGroupId,
  showScopeRelated,
  overrideTraverseOptions = ({ menuConfig }) => menuConfig,
  getDistinctReferenceKey,
  shouldCollapseGraph,
  viewId,
  i18nLocale,
  scopeDiff,
  dataLimitCheck,
}: BuildViewModelWithDataLimitArgs): BuildViewModelResult => {
  const { rawGraph, traversalViewModel } = getRawGraph({
    viewInterface,
    context,
    components,
    scenarioRelatedComponentIds,
    viewSettings,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    isInScopeDiffMode,
    expandedStateChangingGroupId,
    showScopeRelated,
    overrideTraverseOptions,
    getDistinctReferenceKey,
    shouldCollapseGraph,
    viewId,
    i18nLocale,
    scopeDiff,
  });

  const collapsedGroupIds = new Set(viewSettings.collapsedGroupIds);
  const showOnlyConnectedComponents = viewSettings.showOnlyConnectedComponents;

  const dataLimitExceeded = getRefusedDataLimit({
    rawGraph,
    dataLimitCheck,
    viewId,
  });

  return {
    ...viewModelFromGraph({
      viewInterface,
      context,
      collapsedGroupIds,
      showOnlyConnectedComponents,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      isInScopeDiffMode,
      expandedStateChangingGroupId,
      showScopeRelated,
      getDistinctReferenceKey,
      shouldCollapseGraph,
      rawGraph: dataLimitExceeded
        ? getEmptyTraversalAndGroupingResult()
        : rawGraph,
      getGraphNodeOrGroupLabel: getGraphNodeOrGroupLabelForWorkspaceMode,
      getGraphEdgeLabel: (item, isShowReferenceTypeApplied) =>
        getGraphEdgeLabelForWorkspaceMode(
          item,
          resolveEdgeReferenceDisplayLabel(item),
          i18nLocale,
          isShowReferenceTypeApplied
        ),
      i18nLocale,
      scopeDiff,
    }),
    ...traversalViewModel,
    dataLimitExceeded,
  };
};
export { buildViewModel };
